import { setActiveScreen } from "../lib/router";
import type { ScreenName } from "../lib/state";

interface Slide {
  imageUrl: string;
  target: ScreenName;
  alt: string;
  caption: string;
}

const SLIDES: Slide[] = [
  {
    imageUrl: "/gallery/outfit-1.png",
    target: "studio",
    alt: "Woman wearing a styled outfit",
    caption: "Try a new outfit",
  },
  {
    imageUrl: "/gallery/outfit-2.png",
    target: "hair",
    alt: "Hairstyle transformation preview",
    caption: "Try new hair",
  },
  {
    imageUrl: "/gallery/outfit-3.png",
    target: "studio",
    alt: "Another styled outfit look",
    caption: "Endless looks",
  },
  {
    imageUrl: "/gallery/outfit-4.png",
    target: "hair",
    alt: "Restyled hair preview",
    caption: "Restyle in seconds",
  },
    {
    imageUrl: "/gallery/outfit-5.png",
    target: "hair",
    alt: "Restyled hair preview",
    caption: "Restyle in seconds",
  },
    {
    imageUrl: "/gallery/outfit-6.png",
    target: "hair",
    alt: "Restyled hair preview",
    caption: "Restyle in seconds",
  }
];

const SLIDE_INTERVAL_MS = 5000;

function bindHeroRail(): void {
  const headline = document.getElementById("hero-display-text");
  if (!headline) return;
  const defaultText = headline.dataset.default ?? headline.textContent ?? "";
  const buttons = document.querySelectorAll<HTMLButtonElement>(
    ".ios-list-row[data-headline]",
  );
  buttons.forEach(button => {
    const text = button.dataset.headline;
    if (!text) return;
    button.addEventListener("mouseenter", () => {
      headline.textContent = text;
    });
    button.addEventListener("focus", () => {
      headline.textContent = text;
    });
    button.addEventListener("mouseleave", () => {
      headline.textContent = defaultText;
    });
    button.addEventListener("blur", () => {
      headline.textContent = defaultText;
    });
  });
}

export function bind(): void {
  bindHeroRail();

  const root = document.getElementById("hero-gallery");
  const stage = document.getElementById("hero-gallery-stage");
  const dotsEl = document.getElementById("hero-gallery-dots");
  if (!root || !stage || !dotsEl) return;

  let index = 0;
  let timer: number | null = null;
  let hovered = false;
  const slideEls: HTMLButtonElement[] = [];
  const dotEls: HTMLButtonElement[] = [];

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  function stop(): void {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  }

  function start(): void {
    if (prefersReducedMotion || hovered || document.hidden || SLIDES.length < 2) {
      return;
    }
    stop();
    timer = window.setInterval(tick, SLIDE_INTERVAL_MS);
  }

  function go(next: number): void {
    const normalized = ((next % SLIDES.length) + SLIDES.length) % SLIDES.length;
    slideEls.forEach((el, i) => {
      el.classList.toggle("is-active", i === normalized);
    });
    dotEls.forEach((el, i) => {
      const isActive = i === normalized;
      el.setAttribute("aria-current", isActive ? "true" : "false");
      el.tabIndex = isActive ? 0 : -1;
    });
    index = normalized;
  }

  function tick(): void {
    go((index + 1) % SLIDES.length);
  }

  SLIDES.forEach((slide, i) => {
    const slideBtn = document.createElement("button");
    slideBtn.type = "button";
    slideBtn.className = "hero-slide";
    slideBtn.setAttribute("aria-roledescription", "slide");
    slideBtn.setAttribute(
      "aria-label",
      `Slide ${i + 1} of ${SLIDES.length}: ${slide.caption}`,
    );
    if (i === 0) slideBtn.classList.add("is-active");

    const img = document.createElement("img");
    img.src = slide.imageUrl;
    img.alt = "";
    img.decoding = "async";
    if (i === 0) {
      img.fetchPriority = "high";
    } else {
      img.loading = "lazy";
    }
    slideBtn.appendChild(img);

    const caption = document.createElement("span");
    caption.className = "hero-slide-caption";
    caption.textContent = slide.caption;
    slideBtn.appendChild(caption);

    slideBtn.addEventListener("click", () => {
      setActiveScreen(slide.target);
    });

    stage.appendChild(slideBtn);
    slideEls.push(slideBtn);

    const dotBtn = document.createElement("button");
    dotBtn.type = "button";
    dotBtn.className = "hero-dot";
    dotBtn.setAttribute("aria-label", `Show slide ${i + 1}`);
    dotBtn.setAttribute("aria-current", i === 0 ? "true" : "false");
    dotBtn.tabIndex = i === 0 ? 0 : -1;
    dotBtn.addEventListener("click", () => {
      go(i);
      stop();
      start();
    });
    dotsEl.appendChild(dotBtn);
    dotEls.push(dotBtn);
  });

  start();

  root.addEventListener("mouseenter", () => {
    hovered = true;
    stop();
  });
  root.addEventListener("mouseleave", () => {
    hovered = false;
    start();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stop();
    else start();
  });

  root.tabIndex = 0;
  root.addEventListener("keydown", event => {
    if (event.repeat) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (event.key === "ArrowRight") {
      go(index + 1);
      stop();
      start();
      event.preventDefault();
    } else if (event.key === "ArrowLeft") {
      go(index - 1);
      stop();
      start();
      event.preventDefault();
    }
  });
}
